import { useEffect, useState } from "react";
import { Flame, TrendingUp, Zap, Crosshair, Ban } from "lucide-react";
import NegativesEditor, { LiveNegatives } from "@/components/NegativesEditor";
import { Panel, Tag } from "@/components/common";
import { fmtMoney } from "@/lib/api";

const IMPACT = { high: "emerald", medium: "cyan", low: "muted" };

const Finding = ({ f, accent, children, testid }) => (
  <div data-testid={testid} className={`rounded-md border border-[#1e293b] bg-[#0a101d] p-4 shadow-[inset_3px_0_0_${accent}]`} style={{ boxShadow: `inset 3px 0 0 ${accent}` }}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="text-sm font-semibold text-slate-100">{f.title}</div>
      {children}
    </div>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{f.detail}</p>
    {f.evidence && <p className="mt-2 font-mono text-[11px] leading-relaxed text-slate-500">EVIDENCE · {f.evidence}</p>}
    {f.action && <p className="mt-2 text-xs text-emerald-300">→ {f.action}</p>}
  </div>
);

const Section = ({ title, icon, items, accent, testid, render }) => (
  <Panel title={title} icon={icon} testid={testid} right={<span className="font-mono text-xs text-slate-500">{items?.length || 0}</span>}>
    {items?.length ? <div className="space-y-3">{items.map(render)}</div> : <p className="text-sm text-slate-500">Nothing found in the data.</p>}
  </Panel>
);

export default function AuditReport({ result, currency, onSaveNegatives, liveNegatives }) {
  const t = result.inferred_targeting || {};
  const [negs, setNegs] = useState(result.negative_keyword_suggestions || []);
  useEffect(() => setNegs(result.negative_keyword_suggestions || []), [result]);
  return (
    <div className="space-y-6" data-testid="audit-report">
      <Panel testid="audit-summary">
        <div className="mb-3 flex items-center gap-2"><div className="cc-eyebrow">Executive summary</div><Tag tone={result.data_sufficiency === "sufficient" ? "emerald" : "amber"}>Data: {result.data_sufficiency}</Tag></div>
        <p className="text-base leading-relaxed text-slate-200">{result.summary}</p>
      </Panel>
      <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Wasting money" icon={Flame} testid="audit-wasting-money" items={result.wasting_money}
          render={(f, i) => <Finding key={i} f={f} accent="#ef4444" testid={`audit-waste-${i}`}>{f.estimated_waste_30d != null && <Tag tone="red">≈ {fmtMoney(f.estimated_waste_30d, currency)} / 30d</Tag>}</Finding>} />
        <Section title="What's working" icon={TrendingUp} testid="audit-working" items={result.working}
          render={(f, i) => <Finding key={i} f={f} accent="#10b981" testid={`audit-working-${i}`} />} />
      </div>
      <Section title="Quick wins" icon={Zap} testid="audit-quick-wins" items={result.quick_wins}
        render={(f, i) => <Finding key={i} f={f} accent="#06b6d4" testid={`audit-quickwin-${i}`}><div className="flex gap-1.5"><Tag tone={IMPACT[f.impact] || "muted"}>Impact {f.impact}</Tag><Tag>Effort {f.effort}</Tag></div></Finding>} />
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Inferred targeting" icon={Crosshair} testid="audit-inferred-targeting">
          <dl className="space-y-4 text-sm">
            <div><dt className="cc-eyebrow">Cities / areas</dt><dd className="mt-1.5 flex flex-wrap gap-1.5">{(t.cities || []).length ? t.cities.map((c) => <Tag key={c} tone="cyan">{c}</Tag>) : <span className="text-slate-500">Not determinable</span>}</dd></div>
            {t.geo_notes && <div><dt className="cc-eyebrow">Geo notes</dt><dd className="mt-1 text-slate-300">{t.geo_notes}</dd></div>}
            <div><dt className="cc-eyebrow">Budget level</dt><dd className="mt-1 text-slate-300">{t.budget_level || "—"} {t.avg_daily_spend != null && <span className="font-mono text-emerald-300">· {fmtMoney(t.avg_daily_spend, currency)}/day</span>}</dd></div>
            <div><dt className="cc-eyebrow">Audience</dt><dd className="mt-1 text-slate-300">{t.audience || "—"}</dd></div>
            {(t.intent_themes || []).length > 0 && <div><dt className="cc-eyebrow">Intent themes</dt><dd className="mt-1.5 flex flex-wrap gap-1.5">{t.intent_themes.map((c) => <Tag key={c}>{c}</Tag>)}</dd></div>}
          </dl>
        </Panel>
        <Panel title="Negative keyword ideas" icon={Ban} testid="audit-negatives"
          right={onSaveNegatives && <button data-testid="audit-negatives-save" onClick={() => onSaveNegatives(negs)} className="cc-btn cc-btn-primary px-3 py-1.5 text-xs">Save</button>}>
          <NegativesEditor items={negs} onChange={setNegs} testid="audit-negatives-editor" showReason />
        </Panel>
      </div>
      {liveNegatives && (
        <Panel title="Live negatives in your Google Ads account (read-only)" icon={Ban} testid="audit-live-negatives">
          <LiveNegatives items={liveNegatives} testid="audit-live-negatives-list" />
        </Panel>
      )}
    </div>
  );
}
